import { ApolloClient } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_PRODUCT_PRICE,
  GET_MASTER_DATA_PRODUCT_PRICE_HISTORY,
  GET_MASTER_DATA_PRODUCT_PRICES,
} from "@src/domain/graphql/masterData";
import {
  CreatedMasterDataProductPrice,
  MasterDataProductPrice,
  MasterDataProductPriceHistory,
  type ICreatedMasterDataProductPrice,
  type IMasterDataProductPrice,
  type IMasterDataProductPriceHistory,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataProductPriceDTO,
  GetMasterDataProductPriceHistoryDTO,
  GetMasterDataProductPricesDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataProductPriceRepository,
  MasterDataProductPricesListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
  toMasterDataListResult,
} from "./masterDataRepository.shared";

export class MasterDataProductPriceRepository
  implements IMasterDataProductPriceRepository
{
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getProductPrices(
    params: GetMasterDataProductPricesDTO,
  ): Promise<MasterDataProductPricesListResult> {
    const { data, error } = await this.client.query<{
      masterDataProductPrices: {
        items: IMasterDataProductPrice[];
        total: number;
        page: number;
        limit: number;
      };
    }>({
      query: GET_MASTER_DATA_PRODUCT_PRICES,
      variables: {
        params: {
          search: params.search ?? null,
          includeInactive: params.includeInactive ?? true,
          productId: params.productId ?? null,
          page_number: params.page_number,
          page_size: params.page_size,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataProductPrices;
    if (!response) {
      throw new Error("Failed to fetch master data product prices");
    }

    return toMasterDataListResult(
      response,
      (item) =>
        MasterDataProductPrice.from({
          productPriceId: item.productPriceId,
          productId: item.productId,
          productKey: item.productKey ?? null,
          itemName: item.itemName ?? null,
          basePrice: item.basePrice,
          startDate: item.startDate ?? null,
          endDate: item.endDate ?? null,
          isActive: item.isActive ?? null,
          createdAt: item.createdAt ?? null,
          updatedAt: item.updatedAt ?? null,
        }),
      params.page_number,
      params.page_size,
    );
  }

  async getProductPriceHistory(
    params: GetMasterDataProductPriceHistoryDTO,
  ): Promise<MasterDataProductPriceHistory> {
    const { data, error } = await this.client.query<{
      masterDataProductPriceHistory: IMasterDataProductPriceHistory | null;
    }>({
      query: GET_MASTER_DATA_PRODUCT_PRICE_HISTORY,
      variables: {
        params: {
          productId: params.productId,
          purchaserId: params.purchaserId ?? null,
          period: params.period,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataProductPriceHistory;
    if (!response) {
      throw new Error("Failed to fetch product price history");
    }

    return MasterDataProductPriceHistory.from(response);
  }

  async createProductPrice(
    params: CreateMasterDataProductPriceDTO,
  ): Promise<CreatedMasterDataProductPrice> {
    const result = await this.client.mutate<{
      createMasterDataProductPrice: ICreatedMasterDataProductPrice | null;
    }>({
      mutation: CREATE_MASTER_DATA_PRODUCT_PRICE,
      variables: {
        params: {
          productId: params.productId,
          purchaserId: params.purchaserId ?? null,
          productPrice: params.productPrice,
          startDate: params.startDate,
          notes: params.notes ?? null,
        },
      },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return CreatedMasterDataProductPrice.from(
      requireMasterDataMutationData(
        result.data?.createMasterDataProductPrice,
        result.error,
        "Failed to save product price",
      ),
    );
  }
}
